import {
  type AuditItem,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  type TaskDetails,
  type TaskPriority,
  type TaskStatus,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronLeft, Plus, Trash2, X } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { Comments } from '@/components/comments';
import { Deadline, PriorityMark, StatusBadge } from '@/components/task-bits';
import { Avatar, Button, ErrorBox, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { formatMoment } from '@/lib/format';
import { taskQuery } from '@/lib/queries';
import { TaskFormDialog } from './task-form';

export function TaskPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery(taskQuery(id));
  const [editing, setEditing] = useState(false);
  const [addingSub, setAddingSub] = useState(false);
  const qc = useQueryClient();
  // Открытая карточка отмечает комментарии прочитанными — пересчитываем счётчик в меню и списке
  useEffect(() => {
    if (q.dataUpdatedAt) {
      qc.invalidateQueries({ queryKey: ['tasks', 'counters'] });
      qc.invalidateQueries({ queryKey: ['tasks', 'list'] });
    }
  }, [q.dataUpdatedAt, qc]);

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const t = q.data;

  return (
    <div className="max-w-[1180px]">
      <Link to="/tasks" className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink">
        <ChevronLeft className="size-4" aria-hidden />
        Все задачи
      </Link>

      {t.parent && (
        <p className="mb-1 text-[13px] text-steel">
          Подзадача к{' '}
          <Link to="/tasks/$id" params={{ id: t.parent.id }} className="link">
            #{t.parent.number} {t.parent.title}
          </Link>
        </p>
      )}
      {t.template && (
        <p className="mb-1 text-[13px] text-steel">
          Поставлена по расписанию:{' '}
          <Link to="/tasks/recurring" className="link">
            {t.template.title}
          </Link>
        </p>
      )}
      <header className="mb-6 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] tabular-nums text-steel">Задача #{t.number}</p>
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">{t.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={t.status} />
            <PriorityMark priority={t.priority} />
            {t.isOverdue && <span className="text-[13px] font-medium text-danger">Срок прошёл</span>}
          </div>
        </div>
        {t.permissions.canEdit && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Изменить
          </Button>
        )}
      </header>

      {editing && <TaskFormDialog task={t} onClose={() => setEditing(false)} />}
      {addingSub && (
        <TaskFormDialog
          parent={{ id: t.id, number: t.number, title: t.title }}
          onClose={() => setAddingSub(false)}
        />
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          <StatusBar task={t} />

          <section className="panel px-5 py-5">
            {t.description ? (
              <p className="max-w-[75ch] whitespace-pre-wrap break-words">{t.description}</p>
            ) : (
              <p className="text-steel">Описания нет.</p>
            )}
          </section>

          <Checklist task={t} />

          <section className="panel">
            <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
              <h2 className="font-semibold">
                Подзадачи
                {t.subtasks.total > 0 && (
                  <span className="ml-2 font-normal tabular-nums text-steel">
                    {t.subtasks.done} из {t.subtasks.total}
                  </span>
                )}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setAddingSub(true)}>
                <Plus className="size-4" aria-hidden />
                Добавить
              </Button>
            </div>
            {t.subtaskItems.length === 0 ? (
              <p className="px-5 py-4 text-steel">
                Можно разбить задачу на части и поручить их разным людям.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {t.subtaskItems.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
                    <StatusBadge status={s.status} />
                    <Link
                      to="/tasks/$id"
                      params={{ id: s.id }}
                      className="min-w-0 flex-1 truncate hover:text-link"
                    >
                      <span className="tabular-nums text-steel">#{s.number}</span> {s.title}
                    </Link>
                    <Deadline iso={s.deadline} overdue={s.isOverdue} compact />
                    <Avatar name={s.assignee.fullName} userId={s.assignee.userId} size={22} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Comments entityType="task" entityId={t.id} />
        </div>

        <aside className="flex flex-col gap-6">
          <section className="panel px-5 py-4">
            <dl className="flex flex-col gap-3.5">
              <Row label="Срок">
                <Deadline iso={t.deadline} overdue={t.isOverdue} />
              </Row>
              <Row label="Исполнитель">
                <Person name={t.assignee.fullName} userId={t.assignee.userId} />
              </Row>
              <Row label="Постановщик">
                <Person name={t.creator.fullName} userId={t.creator.userId} />
              </Row>
              {t.coexecutors.length > 0 && (
                <Row label="Соисполнители">
                  <div className="flex flex-col gap-1.5">
                    {t.coexecutors.map((c) => (
                      <Person key={c.userId} name={c.fullName} />
                    ))}
                  </div>
                </Row>
              )}
              {t.observers.length > 0 && (
                <Row label="Наблюдатели">
                  <div className="flex flex-col gap-1.5">
                    {t.observers.map((c) => (
                      <Person key={c.userId} name={c.fullName} />
                    ))}
                  </div>
                </Row>
              )}
              <Row label="Проект">{t.project?.name ?? <span className="text-steel">без проекта</span>}</Row>
              <Row label="Приоритет">{TASK_PRIORITY_LABELS[t.priority as TaskPriority]}</Row>
              <Row label="Проверка результата">{t.requireReview ? 'постановщиком' : 'не нужна'}</Row>
              <Row label="Создана">{formatMoment(t.createdAt)}</Row>
              {t.completedAt && <Row label="Завершена">{formatMoment(t.completedAt)}</Row>}
            </dl>
          </section>
          <History taskId={t.id} />
          {t.permissions.canDelete && <DeleteTask task={t} />}
        </aside>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12.5px] text-steel">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function Person({ name, userId }: { name: string; userId?: string }) {
  return (
    <span className="flex items-center gap-2">
      <Avatar name={name} userId={userId} size={24} />
      {name}
    </span>
  );
}

/** Кнопки доступных переходов: «Начать», «Завершить», «Принять работу»… — ровно те, что разрешит сервер. */
function StatusBar({ task }: { task: TaskDetails }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const change = useMutation({
    mutationFn: (status: TaskStatus) => api(`/tasks/${task.id}/status`, { method: 'POST', body: { status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
    onError: (err) => setError(err.message),
  });
  const transitions = task.permissions.transitions;
  if (!transitions.length) return null;

  const hint: Partial<Record<TaskStatus, string>> = {
    review: 'Задача у постановщика на проверке.',
  };

  return (
    <section className="panel flex flex-wrap items-center gap-2 px-5 py-3.5">
      <span className="mr-1 text-[13.5px] text-steel">
        {hint[task.status] ?? `Сейчас: ${TASK_STATUS_LABELS[task.status].toLowerCase()}`}
      </span>
      {transitions.map((tr, i) => (
        <Button
          key={tr.to}
          size="sm"
          variant={i === 0 ? 'primary' : 'secondary'}
          loading={change.isPending && change.variables === tr.to}
          onClick={() => {
            setError(null);
            change.mutate(tr.to);
          }}
        >
          {tr.label}
        </Button>
      ))}
      {error && (
        <p role="alert" className="w-full text-[13px] text-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function Checklist({ task }: { task: TaskDetails }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });
  const editable = task.permissions.canChecklist;
  const toggle = useMutation({
    mutationFn: (v: { id: string; isDone: boolean }) =>
      api(`/tasks/checklist/${v.id}`, { method: 'PATCH', body: { isDone: v.isDone } }),
    onSettled: refresh,
  });
  const add = useMutation({
    mutationFn: (text: string) => api(`/tasks/${task.id}/checklist`, { method: 'POST', body: { text } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/tasks/checklist/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  if (!task.checklistItems.length && !editable) return null;

  function onAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const text = String(new FormData(form).get('text') ?? '').trim();
    if (!text) return;
    add.mutate(text, { onSuccess: () => form.reset() });
  }

  const done = task.checklistItems.filter((c) => c.isDone).length;
  const total = task.checklistItems.length;

  return (
    <section className="panel">
      <div className="flex items-center gap-3 border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Чек-лист</h2>
        {total > 0 && (
          <>
            <span className="tabular-nums text-steel">
              {done} из {total}
            </span>
            <span aria-hidden className="h-1.5 flex-1 overflow-hidden rounded-full bg-sunken">
              <span
                className="block h-full rounded-full bg-ok transition-[width]"
                style={{ width: `${(done / total) * 100}%` }}
              />
            </span>
          </>
        )}
      </div>
      <ul className="px-3 py-2">
        {task.checklistItems.map((c) => (
          <li key={c.id} className="group flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-paper">
            <input
              id={`chk-${c.id}`}
              type="checkbox"
              checked={c.isDone}
              disabled={!editable}
              onChange={(e) => toggle.mutate({ id: c.id, isDone: e.target.checked })}
              className="size-4 accent-[var(--color-ok)]"
            />
            <label htmlFor={`chk-${c.id}`} className={clsx('flex-1', c.isDone && 'text-steel line-through')}>
              {c.text}
            </label>
            {editable && (
              <button
                type="button"
                aria-label={`Удалить пункт «${c.text}»`}
                onClick={() => remove.mutate(c.id)}
                className="text-steel opacity-0 hover:text-danger focus:opacity-100 group-hover:opacity-100"
              >
                <X className="size-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <form onSubmit={onAdd} className="flex gap-2 border-t border-line px-5 py-3">
          <label htmlFor="checklist-new" className="sr-only">
            Новый пункт
          </label>
          <input id="checklist-new" name="text" placeholder="Новый пункт" className="input" maxLength={500} />
          <Button type="submit" size="md" variant="secondary" loading={add.isPending}>
            Добавить
          </Button>
        </form>
      )}
    </section>
  );
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Название',
  description: 'Описание',
  assigneeId: 'Исполнитель',
  projectId: 'Проект',
  priority: 'Приоритет',
  requireReview: 'Проверка результата',
  deadline: 'Срок',
};

function describe(a: AuditItem): string {
  if (a.action === 'created') return 'Создал задачу';
  if (a.action === 'deleted') return 'Удалил задачу';
  if (a.action === 'status_changed' && a.diff?.status) {
    return `Статус: ${TASK_STATUS_LABELS[a.diff.status.to as TaskStatus]?.toLowerCase() ?? a.diff.status.to}`;
  }
  if (a.action === 'updated') {
    const fields = Object.keys(a.diff ?? {}).map((f) => FIELD_LABELS[f] ?? f);
    return fields.length ? `Изменил: ${fields.join(', ').toLowerCase()}` : 'Изменил участников';
  }
  return a.action;
}

function History({ taskId }: { taskId: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['tasks', 'history', taskId],
    queryFn: () => api<AuditItem[]>(`/tasks/${taskId}/history`),
    enabled: open,
  });
  return (
    <section className="panel">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-5 py-3.5 text-left font-semibold"
      >
        История
        <span className="text-[13px] font-normal text-steel">{open ? 'Скрыть' : 'Показать'}</span>
      </button>
      {open &&
        (q.isPending ? (
          <Spinner />
        ) : (
          <ol className="divide-y divide-line border-t border-line">
            {q.data?.map((a) => (
              <li key={a.id} className="px-5 py-2.5 text-[13px]">
                <p>{describe(a)}</p>
                <p className="text-steel">
                  {a.actor?.fullName ?? 'Система'}, {formatMoment(a.at)}
                </p>
              </li>
            ))}
          </ol>
        ))}
    </section>
  );
}

function DeleteTask({ task }: { task: TaskDetails }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () => api(`/tasks/${task.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks'] });
      await navigate({ to: '/tasks' });
    },
  });
  return confirm ? (
    <div className="panel px-5 py-4">
      <p className="font-medium">Удалить задачу #{task.number}?</p>
      <p className="mt-1 text-[13px] text-steel">Участники перестанут её видеть. Подзадачи останутся.</p>
      <div className="mt-3 flex gap-2">
        <Button variant="danger" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}>
          Удалить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
          Отмена
        </Button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setConfirm(true)}
      className="inline-flex items-center gap-1.5 self-start px-1 text-[13px] text-steel hover:text-danger"
    >
      <Trash2 className="size-4" aria-hidden />
      Удалить задачу
    </button>
  );
}
